import { fullStateNameFromAbbreviation } from 'utils'
import { calculateEmployeeMonthlyAllowanceForEmployeeAndDependents } from 'utils/quote_calculations'
import {
  type PutQuotingProspectsIdReasonsForChangingInsuranceItem as ReasonsForChangingInsurance,
  type QuotingPlanAvailability,
  type QuotingQuoteResult,
  type QuotingRecommendedQuoteResults
} from 'utils/web_api.schemas'

import { DEFAULT_COMPLIANCE_FEE, DEFAULT_PER_EMPLOYEE_COST } from './consts'
import { AllowanceStrategies, type AllowanceStrategy, type CoverageLevel, type CoverageLevelPill, type FiltersValues } from './types'

export function calculateTotalMonthlyPlatformFee (enrolledEmployeeCount: number): number {
  return (DEFAULT_PER_EMPLOYEE_COST * enrolledEmployeeCount) + DEFAULT_COMPLIANCE_FEE
}

export function getRecommendedDependentCoverageLevel (reasonsForChangingInsurance: ReasonsForChangingInsurance[] | undefined): number {
  if (!reasonsForChangingInsurance) return 0.5

  const reasons = reasonsForChangingInsurance
  const hasEmployeeHappinessOrBestBenefits = reasons.includes('make_employees_happier') || reasons.includes('best_benefits')
  const wantsToAvoidRateIncreases = reasons.includes('avoid_rate_increases')

  if (!hasEmployeeHappinessOrBestBenefits) return 0.5
  if (wantsToAvoidRateIncreases) return 0.75
  return 1
}

export function getDefaultFilterValues (recommendedDependentCoverageLevel: number): FiltersValues {
  return {
    coverageLevel: 'best_fit',
    employerEmployeeContribution: 1,
    employerSpouseContribution: recommendedDependentCoverageLevel,
    employerChildContribution: recommendedDependentCoverageLevel,
  }
}

export function isFlatRecommended (eligibleEmployees: QuotingQuoteResult[], isLoading: boolean): boolean {
  if (!eligibleEmployees || isLoading) return false
  if (eligibleEmployees.length > 10) return false

  const stateCodes = new Set(eligibleEmployees.map((emp: QuotingQuoteResult) => emp.rating_area_code.substring(0, 2)))
  if (stateCodes.size > 1) return false

  return true
}

export function getRecommendedAllowanceStrategy (isFlatRecommended: boolean): AllowanceStrategy {
  return isFlatRecommended ? AllowanceStrategies.Flat : AllowanceStrategies.Smart
}

export function getCurrentQuoteResults (
  quoteResultsByCoverageLevel: QuotingRecommendedQuoteResults | undefined,
  selectedCoverageLevel: CoverageLevel
): QuotingQuoteResult[] {
  return quoteResultsByCoverageLevel && quoteResultsByCoverageLevel[selectedCoverageLevel]?.quote_results
    ? quoteResultsByCoverageLevel[selectedCoverageLevel]?.quote_results
    : []
}

export function getEligibleEmployees (currentQuoteResults: QuotingQuoteResult[]): QuotingQuoteResult[] {
  return currentQuoteResults.filter((emp: QuotingQuoteResult) => emp.eligible) ?? []
}

export function getIneligibleEmployees (currentQuoteResults: QuotingQuoteResult[]): QuotingQuoteResult[] {
  return currentQuoteResults.filter((emp: QuotingQuoteResult) => !emp.eligible) ?? []
}

export function getWillEnrollEmployees (
  allowanceStrategy: AllowanceStrategy,
  currentQuoteResults: QuotingQuoteResult[],
  flatAllowanceEmployees: QuotingQuoteResult[] | undefined,
  willEnrollEmployeeIds: Set<string>
): QuotingQuoteResult[] {
  if (allowanceStrategy === AllowanceStrategies.Flat) {
    return (flatAllowanceEmployees ?? []).filter((employee: QuotingQuoteResult) => willEnrollEmployeeIds.has(employee.id))
  }
  return (currentQuoteResults ?? []).filter((employee: QuotingQuoteResult) => willEnrollEmployeeIds.has(employee.id))
}

export function getPlanAvailabilityForEmployee (
  employee: QuotingQuoteResult,
  planAvailability: QuotingPlanAvailability[] | undefined
): QuotingPlanAvailability | undefined {
  if (!planAvailability) return undefined
  return planAvailability.find((availability: QuotingPlanAvailability) => availability.rating_area_code === employee.rating_area_code)
}

export function getTotalPlansForEmployee (
  employee: QuotingQuoteResult,
  planAvailability: QuotingPlanAvailability[] | undefined
): number {
  const availability = getPlanAvailabilityForEmployee(employee, planAvailability)
  if (!availability?.plan_counts) return 0
  return Object.values(availability.plan_counts).reduce((sum, count) => sum + count, 0)
}

export function getMonthlyAllowanceForEmployee (
  employeeId: string,
  allowanceStrategy: AllowanceStrategy,
  flatAllowanceCosts: { costsByEmployeeId: Record<string, { employerCost: number }> },
  filterValues: FiltersValues,
  currentQuoteResults: QuotingQuoteResult[]
): number {
  if (allowanceStrategy === AllowanceStrategies.Flat) {
    return flatAllowanceCosts.costsByEmployeeId[employeeId]?.employerCost ?? 0
  }

  const employee = currentQuoteResults.find((employee: QuotingQuoteResult) => employee.id === employeeId)
  if (!employee) return 0

  return calculateEmployeeMonthlyAllowanceForEmployeeAndDependents(employee, {
    employerEmployeeContribution: filterValues.employerEmployeeContribution,
    employerSpouseContribution: filterValues.employerSpouseContribution,
    employerChildContribution: filterValues.employerChildContribution,
  })
}

export function getCoverageLevelPills (
  selectedCoverageLevel: CoverageLevel,
  reasonsForChangingInsurance: ReasonsForChangingInsurance[] | undefined,
  offersHealthInsuranceToday: boolean | undefined,
  eligibleEmployees: QuotingQuoteResult[]
): CoverageLevelPill[] {
  const pills: CoverageLevelPill[] = []

  if (selectedCoverageLevel === 'lowest_cost') {
    pills.push({ text: 'Less affordable for employees', isPositive: false })
    if (reasonsForChangingInsurance?.includes('make_employees_happier') || reasonsForChangingInsurance?.includes('best_benefits')) {
      pills.push({ text: 'Not aligned with your reasons for changing insurance', isPositive: false })
    }
    pills.push({ text: 'Less expensive for you', isPositive: true })

    if (reasonsForChangingInsurance?.includes('avoid_rate_increases')) {
      pills.push({ text: 'Controls costs', isPositive: true })
    }
  } else if (selectedCoverageLevel === 'better_benefits') {
    pills.push({ text: 'Better benefits for employees', isPositive: true })

    if (reasonsForChangingInsurance?.includes('make_employees_happier')) {
      pills.push({ text: 'Aligned with making employees happier', isPositive: true })
    }
  } else {
    if (offersHealthInsuranceToday) {
      pills.push({ text: 'Closest to current benefits', isPositive: true })
    }

    const stateCodes = new Set(eligibleEmployees.map((emp: QuotingQuoteResult) => emp.rating_area_code.substring(0, 2)))

    if (stateCodes.size === 1) {
      const stateCode = [...stateCodes][0]
      const stateName = fullStateNameFromAbbreviation(stateCode)
      pills.push({ text: `All employees in ${stateName}`, isPositive: true })
    } else {
      pills.push({ text: 'Employees in multiple states', isPositive: true })
    }

    pills.push({ text: 'Balance of flexibility and affordability', isPositive: true })
  }

  return pills
}

export function getBenchmarkAvailabilityFlags (quoteResultsByCoverageLevel: QuotingRecommendedQuoteResults | undefined) {
  return {
    hasLowestCostBenchmark: !!(quoteResultsByCoverageLevel && quoteResultsByCoverageLevel.lowest_cost),
    hasBetterBenefitsBenchmark: !!(quoteResultsByCoverageLevel && quoteResultsByCoverageLevel.better_benefits),
  }
}
